import { type MouseEvent, FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';
// Import Styles
import styles from './MarkDownReader.module.scss';

interface Props {
    href?: string;
    /** A footnote reference has one: the back link (↩) of its note jumps to it. */
    id?: string;
    children?: ReactNode;
}

/** Resolves a Markdown href against the current page; null when it is not a valid URL. */
const parseHref = (href: string): URL | null => {
    try {
        return new URL(href, window.location.href);
    } catch {
        return null;
    }
};

/**
 * Scrolls an in-page anchor ("#…", e.g. a footnote and its back link) into view. The page does not jump to it
 * itself: the article scrolls in a custom scroll container, and a hash in the URL would reach the router.
 */
const handleAnchorClick = (e: MouseEvent<HTMLAnchorElement>, href: string) => {
    const fragment = href.slice(1);
    let id = fragment;

    // A malformed escape ("#%zz") throws; such a fragment is looked up as it is written
    try {
        id = decodeURIComponent(fragment);
    } catch {
        // Keeps the raw fragment
    }

    const target = document.getElementById(id);

    if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
};

/**
 * Renders a Markdown hyperlink.
 * Same-origin targets go through react-router's <Link> so the SPA is not
 * reloaded, external pages open in a new tab, in-page anchors scroll to their
 * target, and mailto: / tel: stay plain anchors handled by the browser.
 */
const MDLink: FC<Props> = ({ href = '', id, children }) => {
    // In-page anchors ("#section") need no navigation, so they are never resolved into a route
    const isAnchor = href.startsWith('#');
    const url = isAnchor ? null : parseHref(href);

    if (url && url.origin === window.location.origin) {
        return (
            <Link id={id} className={styles.link} to={`${url.pathname}${url.search}${url.hash}`}>
                {children}
            </Link>
        );
    }

    // Only real web pages are worth a new tab — mailto:/tel:/anchors are not.
    const isWebPage = url?.protocol === 'http:' || url?.protocol === 'https:';

    return (
        <a
            id={id}
            className={styles.link}
            href={href}
            target={isWebPage ? '_blank' : undefined}
            rel={isWebPage ? 'noopener noreferrer' : undefined}
            onClick={isAnchor ? (e) => handleAnchorClick(e, href) : undefined}
        >
            {children}
        </a>
    );
};

export default MDLink;
